import { useState, useEffect } from 'react'
import { X, Key, Plus, Trash2, CheckCircle, Copy } from 'lucide-react'
import { DriverCreateRequest, VehicleItem, driverApi } from '@/api/drivers'
import { Tenant, Driver, UserRole, DriverApprovalStatus, DriverCategory } from '@/types'
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"

interface DriverDrawerProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: DriverCreateRequest & { tenant_id?: string; approval_status?: DriverApprovalStatus }) => Promise<void>
  tenants: Tenant[]
  driver?: Driver | null
  defaultTenantId?: string
}

const DEFAULT_VEHICLE_OPTIONS = ['房車', 'SUV', '廂型車', 'MPV']

export default function DriverDrawer({ isOpen, onClose, onSubmit, tenants, driver, defaultTenantId }: DriverDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [userRole, setUserRole] = useState<UserRole | null>(null)
  const [userTenantId, setUserTenantId] = useState<string | null>(null)
  const [localTenant, setLocalTenant] = useState<{ id: string; name: string; code: string } | null>(() => {
    try {
      const t = JSON.parse(localStorage.getItem('tenant') || 'null')
      return t?.id && t?.name ? { id: t.id, name: t.name, code: t.code || '' } : null
    } catch { return null }
  })
  const [vehicleOptions, setVehicleOptions] = useState<string[]>(DEFAULT_VEHICLE_OPTIONS)
  const [formData, setFormData] = useState<DriverCreateRequest & { approval_status?: DriverApprovalStatus }>({
    name: '',
    phone: '',
    vehicle_type: '',
    vehicle_plate: '',
    vehicle_color: '',
    vehicle_brand_model: '',
    engine_number: '',
    license_number: '',
    license_expiry: '',
    address: '',
    vehicle_inspection_date: '',
    approval_status: DriverApprovalStatus.PENDING,
    driver_category: DriverCategory.COMPANY,
    sort_order: null,
  })
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false)
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false)
  const [vehicles, setVehicles] = useState<VehicleItem[]>([])
  const [newVehicle, setNewVehicle] = useState<VehicleItem>({ plate: '', brand_model: '', color: '', type: '' })
  const [switchingPlate, setSwitchingPlate] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)

  // Get user info and tenant vehicle options from localStorage
  useEffect(() => {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        const userData = JSON.parse(userStr)
        setUserRole(userData.role)
        setUserTenantId(userData.tenant_id)
      } catch (error) {
        console.error('Failed to parse user data:', error)
      }
    }
    const tenantStr = localStorage.getItem('tenant')
    if (tenantStr) {
      try {
        const t = JSON.parse(tenantStr)
        if (t.vehicle_categories?.length) {
          setVehicleOptions(t.vehicle_categories.map((c: any) => typeof c === 'string' ? c : c.name || '').filter(Boolean))
        }
        if (t.id && t.name) {
          setLocalTenant({ id: t.id, name: t.name, code: t.code || '' })
        }
      } catch {}
    }
  }, [])

  // Set default tenant when tenants load
  useEffect(() => {
    if (tenants.length > 0 && !selectedTenantId) {
      // tenant_admin 固定顯示該商戶
      if (userRole === UserRole.TENANT_ADMIN && userTenantId) {
        setSelectedTenantId(userTenantId)
      } else {
        setSelectedTenantId(tenants[0].id)
      }
    }
  }, [tenants, userRole, userTenantId])

  // Reset form when drawer opens/closes or when editing driver
  useEffect(() => {
    if (isOpen) {
      if (driver) {
        // 編輯模式：預填表單
        setFormData({
          name: driver.name,
          phone: driver.phone,
          vehicle_type: driver.vehicle_type,
          vehicle_plate: driver.vehicle_plate,
          vehicle_color: driver.vehicle_color || '',
          vehicle_brand_model: driver.vehicle_brand_model || '',
          engine_number: driver.engine_number || '',
          license_number: driver.license_number,
          license_expiry: driver.license_expiry,
          address: driver.address || '',
          vehicle_inspection_date: driver.vehicle_inspection_date
            ? driver.vehicle_inspection_date.substring(0, 10)
            : '',
          approval_status: driver.approval_status,
          driver_category: driver.driver_category || DriverCategory.COMPANY,
          sort_order: driver.sort_order ?? null,
        })
        // 確保目前使用中車輛也在清單裡
        const existingVehicles = driver.vehicles || []
        const activeInList = existingVehicles.some(v => v.plate === driver.vehicle_plate)
        if (!activeInList && driver.vehicle_plate) {
          const activeVehicle = {
            plate: driver.vehicle_plate,
            brand_model: driver.vehicle_brand_model || '',
            color: driver.vehicle_color || '',
            type: driver.vehicle_type || '',
          }
          const merged = [activeVehicle, ...existingVehicles]
          setVehicles(merged)
          // 同步更新後端
          driverApi.updateVehicles(driver.id, merged)
        } else {
          setVehicles(existingVehicles)
        }
        setSelectedTenantId(driver.tenant_id)
      } else {
        // 新增模式：重置表單
        setFormData({
          name: '',
          phone: '',
          vehicle_type: vehicleOptions[0] || '',
          vehicle_plate: '',
          vehicle_color: '',
          vehicle_brand_model: '',
          engine_number: '',
          license_number: '',
          license_expiry: '',
          address: '',
          vehicle_inspection_date: '',
          approval_status: DriverApprovalStatus.PENDING,
          driver_category: DriverCategory.COMPANY,
          sort_order: null,
        })
        // 優先 defaultTenantId → localTenant(tenant_admin) → 第一筆
        if (defaultTenantId) {
          setSelectedTenantId(defaultTenantId)
        } else if (userRole === UserRole.TENANT_ADMIN && (userTenantId || localTenant?.id)) {
          setSelectedTenantId(userTenantId || localTenant!.id)
        } else if (tenants.length > 0) {
          setSelectedTenantId(tenants[0].id)
        }
      }
    }
  }, [isOpen, driver, tenants, userRole, userTenantId])

  const [submitError, setSubmitError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')
    try {
      await onSubmit({
        ...formData,
        tenant_id: selectedTenantId || undefined,
      })
      onClose()
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || '儲存失敗，請稍後再試'
      setSubmitError(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResetPassword = async () => {
    if (!driver) {
      alert('請先儲存司機資料')
      return
    }

    const tenant = tenants.find(t => t.id === selectedTenantId)
    if (!tenant || !tenant.tax_id) {
      alert('找不到商戶稅號')
      return
    }

    setResetPasswordLoading(true)
    try {
      await driverApi.resetDriverPassword(driver.id, tenant.tax_id)
      alert(`密碼已重設為：${tenant.tax_id}`)
      setShowResetPasswordConfirm(false)
    } catch (error: any) {
      console.error('Failed to reset password:', error)
      alert('重設密碼失敗，請稍後再試')
    } finally {
      setResetPasswordLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity"
        onClick={onClose}
      />

      <div className="fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-[500px] bg-white dark:bg-gray-800 shadow-xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-3 border-b flex items-center justify-between bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white px-1">
            {driver ? '編輯司機' : '新增司機'}
          </h2>
          <div className="flex items-center gap-1.5">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={onClose} 
              className="h-8 px-3 text-xs text-gray-500 hover:text-gray-700"
            >
              取消
            </Button>
            <Button
              type="submit"
              form="driver-form"
              disabled={isSubmitting}
              className="h-8 px-4 text-xs font-bold shadow-sm"
            >
              {isSubmitting ? '儲存中...' : driver ? '更新司機' : '建立司機'}
            </Button>
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-700 mx-1" />
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <form id="driver-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
          {submitError && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
              <span className="shrink-0 font-bold">!</span>
              <span>{submitError}</span>
            </div>
          )}
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">所屬商戶 *</Label>
              {userRole === UserRole.SUPER_ADMIN ? (
                <select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  required
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="" disabled>請選擇商戶</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.code})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex h-9 w-full items-center rounded-md border border-input bg-gray-50 px-3 py-1 text-sm text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  {localTenant
                    ? `${localTenant.name} (${localTenant.code})`
                    : tenants.find(t => t.id === selectedTenantId)
                      ? `${tenants.find(t => t.id === selectedTenantId)!.name} (${tenants.find(t => t.id === selectedTenantId)!.code})`
                      : '載入中...'}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">姓名 *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">電話 *</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>
            <div className="col-span-2">
              <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">地址</Label>
              <Input
                value={formData.address || ''}
                placeholder="司機居住地址"
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">司機類別 *</Label>
              <select
                value={formData.driver_category || DriverCategory.COMPANY}
                onChange={(e) => setFormData({ ...formData, driver_category: e.target.value as DriverCategory })}
                required
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value={DriverCategory.COMPANY}>公司司機</option>
                <option value={DriverCategory.EXTERNAL}>外派司機</option>
              </select>
            </div>
          </div>

          {driver && formData.driver_category === DriverCategory.EXTERNAL && driver.sign_token && (
            <div className="bg-purple-50/50 dark:bg-purple-900/10 p-3 rounded-lg border border-purple-100/50 dark:border-purple-900/30">
              <p className="text-xs text-purple-900 dark:text-purple-100 mb-2">
                外派司機公用簽名連結（免登入，可重複使用）
              </p>
              <Button
                type="button"
                onClick={async () => {
                  const url = `${window.location.origin}/driver-sign/${driver.sign_token}`
                  await navigator.clipboard.writeText(url)
                  setLinkCopied(true)
                  setTimeout(() => setLinkCopied(false), 2000)
                }}
                className="w-full h-8 text-xs bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center gap-2"
              >
                <Copy className="w-3.5 h-3.5" />
                {linkCopied ? '已複製連結！' : '複製簽名連結'}
              </Button>
            </div>
          )}

          {driver && (
            <div className="bg-amber-50/50 dark:bg-amber-900/10 p-3 rounded-lg border border-amber-100/50 dark:border-amber-900/30">
              <p className="text-xs text-amber-900 dark:text-amber-100 mb-2">
                司機登入密碼：{tenants.find(t => t.id === selectedTenantId)?.tax_id || '商戶統編'}
              </p>
              <Button
                type="button"
                onClick={() => setShowResetPasswordConfirm(true)}
                className="w-full h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white flex items-center justify-center gap-2"
              >
                <Key className="w-3.5 h-3.5" />
                重設預設密碼
              </Button>
            </div>
          )}

          <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg space-y-4 border border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">車輛資料（使用中）</h3>
              {driver && (
                vehicles.length > 1 ? (
                  <select
                    className="h-7 text-xs border border-blue-300 rounded-md px-2 text-blue-700 bg-blue-50 cursor-pointer"
                    value={formData.vehicle_plate}
                    disabled={!!switchingPlate}
                    onChange={async (e) => {
                      const plate = e.target.value
                      const v = vehicles.find(x => x.plate === plate)
                      if (!v) return
                      setSwitchingPlate(plate)
                      try {
                        await driverApi.setActiveVehicle(driver.id, plate)
                        setFormData(prev => ({ ...prev, vehicle_plate: v.plate, vehicle_brand_model: v.brand_model || '', vehicle_color: v.color || '', vehicle_type: v.type || prev.vehicle_type }))
                      } catch (err: any) {
                        alert(err?.response?.data?.detail || '切換車輛失敗，請稍後再試')
                      } finally {
                        setSwitchingPlate(null)
                      }
                    }}
                  >
                    {vehicles.map(v => (
                      <option key={v.plate} value={v.plate}>{v.plate}{v.brand_model ? ` · ${v.brand_model}` : ''}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-xs text-gray-400">於下方「車輛清單」新增第二台後可切換</span>
                )
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">車輛類型 *</Label>
                <select
                  value={formData.vehicle_type}
                  onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">選擇車型 *</option>
                  {vehicleOptions.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">車牌號碼 *</Label>
                <Input
                  value={formData.vehicle_plate}
                  onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">車輛顏色</Label>
                <Input
                  value={formData.vehicle_color || ''}
                  onChange={(e) => setFormData({ ...formData, vehicle_color: e.target.value })}
                  placeholder="選填"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">廠牌型號</Label>
                <Input
                  value={formData.vehicle_brand_model || ''}
                  onChange={(e) => setFormData({ ...formData, vehicle_brand_model: e.target.value })}
                  placeholder="例：Toyota Camry"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">引擎號碼</Label>
                <Input
                  value={formData.engine_number || ''}
                  onChange={(e) => setFormData({ ...formData, engine_number: e.target.value })}
                  placeholder="選填"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">審核狀態 *</Label>
                <select
                  value={formData.approval_status || DriverApprovalStatus.PENDING}
                  onChange={(e) => setFormData({ ...formData, approval_status: e.target.value as DriverApprovalStatus })}
                  required
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value={DriverApprovalStatus.PENDING}>待審核</option>
                  <option value={DriverApprovalStatus.APPROVED}>已通過</option>
                  <option value={DriverApprovalStatus.REJECTED}>已駁回</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-blue-50/30 dark:bg-blue-900/10 p-4 rounded-lg space-y-4 border border-blue-100/50 dark:border-blue-900/30">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">證照資料</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">駕照號碼</Label>
                <Input
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">駕照截止日期</Label>
                <Input
                  type="date"
                  value={formData.license_expiry}
                  onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })}
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">車輛檢驗日期</Label>
                <Input
                  type="date"
                  value={formData.vehicle_inspection_date || ''}
                  onChange={(e) => setFormData({ ...formData, vehicle_inspection_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-[11px] font-bold text-gray-500 uppercase tracking-wider">選單排序</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="數字小的排前面，空白排最後"
                  value={formData.sort_order ?? ''}
                  onChange={(e) => setFormData({ ...formData, sort_order: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                />
              </div>
            </div>
          </div>

          {/* 多車輛管理（編輯模式才顯示） */}
          {driver && (
            <div className="bg-orange-50/30 dark:bg-orange-900/10 p-4 rounded-lg space-y-3 border border-orange-100/50 dark:border-orange-900/30">
              <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-100">車輛清單</h3>
              {vehicles.length > 0 ? (
                <div className="space-y-2">
                  {vehicles.map((v) => {
                    const isActive = v.plate === formData.vehicle_plate
                    return (
                      <div key={v.plate} className={`flex items-center gap-2 p-2 rounded-lg border text-sm ${isActive ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-600'}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-gray-800 dark:text-gray-200">{v.plate}</div>
                          <div className="text-xs text-gray-500">{[v.brand_model, v.color, v.type].filter(Boolean).join(' · ')}</div>
                        </div>
                        {isActive ? (
                          <span className="text-xs text-green-600 font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" />使用中</span>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            disabled={switchingPlate === v.plate}
                            onClick={async () => {
                              setSwitchingPlate(v.plate)
                              try {
                                await driverApi.setActiveVehicle(driver.id, v.plate)
                                setFormData(prev => ({ ...prev, vehicle_plate: v.plate, vehicle_brand_model: v.brand_model || '', vehicle_color: v.color || '', vehicle_type: v.type || prev.vehicle_type }))
                              } catch (e: any) {
                                alert(e?.response?.data?.detail || '切換車輛失敗，請稍後再試')
                              } finally {
                                setSwitchingPlate(null)
                              }
                            }}
                          >
                            {switchingPlate === v.plate ? '切換中...' : '切換'}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          onClick={async () => {
                            if (isActive) { alert('無法刪除使用中車輛'); return }
                            const updated = vehicles.filter(x => x.plate !== v.plate)
                            setVehicles(updated)
                            await driverApi.updateVehicles(driver.id, updated)
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400">尚無其他車輛，可於下方新增</p>
              )}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Input placeholder="車牌 *" value={newVehicle.plate} onChange={e => setNewVehicle(p => ({ ...p, plate: e.target.value }))} className="h-8 text-xs" />
                <Input placeholder="廠牌車型" value={newVehicle.brand_model || ''} onChange={e => setNewVehicle(p => ({ ...p, brand_model: e.target.value }))} className="h-8 text-xs" />
                <Input placeholder="顏色" value={newVehicle.color || ''} onChange={e => setNewVehicle(p => ({ ...p, color: e.target.value }))} className="h-8 text-xs" />
                <Input placeholder="車型（房車/SUV…）" value={newVehicle.type || ''} onChange={e => setNewVehicle(p => ({ ...p, type: e.target.value }))} className="h-8 text-xs" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full h-8 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                disabled={!newVehicle.plate}
                onClick={async () => {
                  if (vehicles.find(v => v.plate === newVehicle.plate)) { alert('此車牌已存在'); return }
                  const updated = [...vehicles, { ...newVehicle }]
                  setVehicles(updated)
                  await driverApi.updateVehicles(driver.id, updated)
                  setNewVehicle({ plate: '', brand_model: '', color: '', type: '' })
                }}
              >
                <Plus className="w-3 h-3 mr-1" />新增車輛
              </Button>
            </div>
          )}

        </form>

        {showResetPasswordConfirm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-sm w-full p-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">確認重設密碼</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                確定要將此司機的登入密碼重設為商戶統編嗎？
              </p>
              <p className="text-sm font-mono bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded text-gray-900 dark:text-white">
                新密碼：{tenants.find(t => t.id === selectedTenantId)?.tax_id}
              </p>
              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowResetPasswordConfirm(false)}
                  className="px-4 py-2"
                >
                  取消
                </Button>
                <Button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resetPasswordLoading}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {resetPasswordLoading ? '重設中...' : '確認重設'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
